import { type ReactElement, type ReactNode } from "react";
import { useInRouterContext, useNavigate } from "react-router-dom";
import { ChevronRight, Info, Link2 } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import {
  SectionHeader,
  SettingsMetaCard,
  SettingsPageLayout,
  cn,
  type SettingsMetaLink,
} from "@playbookai/ui";
import { ADMIN_SECTION_ROUTES } from "../../routes.js";
import { ADMIN_NAV } from "./AdminPage.js";

/* The one way an admin section lays itself out: an optional breadcrumb, the
   Direction B page header, then one fluid content column. Admin Console no
   longer renders the Settings-style right meta section or its responsive
   Details drawer; those remain available to personal Settings only. */
export interface AdminSectionProps {
  title: string;
  description: string;
  icon?: LucideIcon;
  /** Trailing header slot — the section's primary action. */
  action?: ReactNode;
  badge?: ReactNode;
  /** Rendered above the header on a sub-page (Directory group roles, one
   *  integration), where the way back matters more than the rail. */
  breadcrumb?: ReactNode;
  /**
   * Section context retained while its content is repurposed or removed from
   * individual pages. AdminSection deliberately does not render it.
   */
  meta: ReactNode;
  children: ReactNode;
  className?: string;
  "data-testid"?: string;
}

export function AdminSection({
  title,
  description,
  icon: Icon,
  action,
  badge,
  breadcrumb,
  children,
  className,
  "data-testid": testId,
}: AdminSectionProps): ReactElement {
  return (
    <div
      className={cn("pb-motion-content-enter", className)}
      data-testid={testId}
    >
      {breadcrumb ? <div className="mb-4">{breadcrumb}</div> : null}
      <SectionHeader
        variant="settings"
        leading={
          Icon ? <Icon className="w-4 h-4 text-pb-cyan-text" /> : undefined
        }
        title={title}
        description={description}
        actionsSlot={action}
        badge={badge}
      />
      <SettingsPageLayout
        // Admin tables and forms use the full lane; no right meta section.
        mainWidth="fluid"
      >
        {children}
      </SettingsPageLayout>
    </div>
  );
}

export interface AdminRelatedCardProps {
  /** Rail ids (`ADMIN_NAV`) to point at, in the order they should read. */
  ids: readonly string[];
  title?: string;
}

interface RelatedLink {
  readonly id: string;
  readonly label: string;
  readonly icon: LucideIcon | undefined;
  readonly route: string;
}

/** Rail entries for the requested ids, in order. An id the rail does not know
 * is skipped, so a renamed section cannot leave a dead pointer behind. */
function relatedLinks(ids: readonly string[]): RelatedLink[] {
  return ids.flatMap((id) => {
    const item = ADMIN_NAV.find((entry) => entry.id === id);
    const route = ADMIN_SECTION_ROUTES[id];
    if (!item || !route) return [];
    return [{ id, label: item.label, icon: item.icon, route }];
  });
}

function relatedCard(
  title: string,
  links: readonly RelatedLink[],
  target: (link: RelatedLink) => Pick<SettingsMetaLink, "href" | "onClick">,
): ReactElement | null {
  if (links.length === 0) return null;
  const rows: SettingsMetaLink[] = links.map((link) => ({
    label: link.label,
    ...(link.icon ? { icon: link.icon } : {}),
    accessory: <ChevronRight className="h-3.5 w-3.5" aria-hidden />,
    ...target(link),
  }));
  return <SettingsMetaCard icon={Link2} title={title} links={rows} />;
}

/** Inside the app's router: buttons that navigate in place. An `<a href>`
 * would reload the SPA; the Settings pages' own admin pointer makes the same
 * call (PB-5134). */
function RoutedRelatedCard({
  ids,
  title,
}: {
  ids: readonly string[];
  title: string;
}): ReactElement | null {
  const navigate = useNavigate();
  return relatedCard(title, relatedLinks(ids), (link) => ({
    onClick: () => navigate(link.route),
  }));
}

/** The "Related" meta card: in-app pointers to sibling admin sections,
 * labelled and iconed from the rail registry so the aside and the rail never
 * disagree on a name. A section rendered on its own — outside the app's
 * router, as its unit spec mounts it — gets the same card with plain links,
 * because `useNavigate` has nothing to navigate with there. */
export function AdminRelatedCard({
  ids,
  title = "Related",
}: AdminRelatedCardProps): ReactElement | null {
  const routed = useInRouterContext();
  if (routed) return <RoutedRelatedCard ids={ids} title={title} />;
  return relatedCard(title, relatedLinks(ids), (link) => ({
    href: link.route,
  }));
}

export interface AdminAboutCardProps {
  title?: string;
  /** One or two sentences a reader can verify against the section itself. */
  children: ReactNode;
}

/** The "About" meta card: what this section is for, in copy the section's own
 * behaviour backs. No numbers here — facts with a source get their own rows. */
export function AdminAboutCard({
  title = "About this section",
  children,
}: AdminAboutCardProps): ReactElement {
  return <SettingsMetaCard icon={Info} title={title} description={children} />;
}
